import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Flame, Car, Scale, LifeBuoy, Sparkle,
  Star, ChevronRight,
  Sparkles, Users, Handshake, Award
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Breadcrumb } from "../lib/landingShared";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

// JSON-LD per SEO locale (LocalBusiness + FAQPage)
const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "InsuranceAgency",
      "@id": "https://dapraassicurazioni.it/#agency",
      "name": "Da Pra Assicurazioni",
      "description": "Agenzia assicurativa plurimandataria a Milano. Da tre generazioni offriamo preventivi auto, casa, vita e business personalizzati.",
      "url": "https://dapraassicurazioni.it",
      "logo": "https://dapraassicurazioni.it/logo.svg",
      "telephone": "+390220177",
      "email": "info@dapraassicurazioni.it",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Via Lambro, 4",
        "addressLocality": "Milano",
        "postalCode": "20129",
        "addressCountry": "IT",
      },
      "areaServed": { "@type": "City", "name": "Milano" },
      "priceRange": "€€",
      "foundingDate": "1958",
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Quanto costa una polizza auto a Milano?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Il costo di una polizza auto a Milano varia in base alla classe di merito, all'età del conducente, al modello del veicolo, ai chilometri percorsi e alle garanzie scelte. In media a Milano la RCA parte da circa 350-450 € all'anno per i profili più virtuosi (classe 1, conducente esperto, vettura utilitaria) e può superare i 1.200 € per i neopatentati o veicoli ad alta cilindrata. Da Pra Assicurazioni confronta le offerte delle migliori compagnie per trovare il preventivo più conveniente sul tuo profilo.",
          },
        },
        {
          "@type": "Question",
          "name": "Posso cambiare compagnia assicurativa?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sì. Grazie alla legge Bersani puoi cambiare compagnia in qualsiasi momento alla scadenza della polizza, senza penali. Il tacito rinnovo è abolito dal 2013: il contratto cessa automaticamente alla scadenza. Da Pra Assicurazioni si occupa di tutta la procedura: calcoliamo il risparmio, gestiamo i tempi di disdetta e attiviamo la nuova polizza senza interruzioni di copertura.",
          },
        },
        {
          "@type": "Question",
          "name": "Come funziona la classe di merito?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "La classe di merito è un sistema bonus-malus che premia gli automobilisti virtuosi. Si parte dalla classe 14 (peggiore) e si scala verso la classe 1 (migliore): ogni anno senza sinistri si scende di una classe, ogni sinistro con colpa fa risalire di due classi. Più bassa è la classe, più bassa è la RCA. La classe di merito è personale e si può trasferire a un familiare convivente grazie alla Legge Bersani.",
          },
        },
      ],
    },
  ],
};

const BENEFITS = [
  { icon: ShieldCheck, title: "RCA",                desc: "Responsabilità Civile obbligatoria, modulabile su massimali e franchigie." },
  { icon: Flame,       title: "Furto e incendio",   desc: "Copertura completa contro furto, tentato furto, rapina e incendio." },
  { icon: Car,         title: "Kasko",              desc: "Protezione totale anche per danni causati al proprio veicolo." },
  { icon: Sparkle,     title: "Cristalli",          desc: "Riparazione o sostituzione di parabrezza e cristalli, senza franchigia." },
  { icon: Scale,       title: "Tutela legale",      desc: "Assistenza legale per controversie stradali e amministrative." },
  { icon: LifeBuoy,    title: "Assistenza stradale", desc: "Soccorso h24, auto sostitutiva e rimpatrio in tutta Europa." },
];

const REVIEWS = [
  {
    name: "Lucia Galanti",
    role: "Cliente da anni",
    text: "Cliente da anni e mi sono sempre trovata benissimo. Professionali, disponibili e molto competenti. Sempre chiari e veloci nel trovare la soluzione migliore. Assolutamente consigliati!",
    stars: 5,
  },
  {
    name: "Francesco Fusi",
    role: "Cliente da anni",
    text: "Cliente da anni e sempre soddisfatto. La differenza la fanno soprattutto le consulenze: ti spiegano tutto in modo chiaro e ti aiutano davvero a scegliere la soluzione migliore.",
    stars: 5,
  },
  {
    name: "Avv. Paola Rossini",
    role: "Recensione Google",
    text: "Grande professionalità, disponibilità e trasparenza fin dal primo contatto.",
    stars: 5,
  },
  {
    name: "Antonella Salvadori",
    role: "Cliente storica",
    text: "Sono cliente di questa agenzia da molti anni e ho sempre trovato grande professionalità, disponibilità e cortesia.",
    stars: 5,
  },
];

const FAQS = [
  {
    q: "Quanto costa una polizza auto a Milano?",
    a: "Il costo di una polizza auto a Milano dipende da diversi fattori: classe di merito, età ed esperienza del conducente, modello e cilindrata del veicolo, chilometri percorsi, garanzie accessorie scelte. In media, a Milano una RCA parte da 350-450 € all'anno per profili virtuosi (classe 1, conducente esperto, vettura utilitaria) e può salire fino a 1.200 € o più per neopatentati o vetture ad alte prestazioni. In qualità di agenzia assicurativa plurimandataria, confrontiamo le offerte delle principali compagnie e troviamo la soluzione più conveniente per il tuo profilo.",
  },
  {
    q: "Posso cambiare compagnia assicurativa?",
    a: "Sì. Dal 2013 il tacito rinnovo è abolito: il contratto cessa automaticamente alla scadenza. Grazie alla Legge Bersani puoi cambiare compagnia in qualsiasi momento alla scadenza della polizza, senza penali e mantenendo la tua classe di merito. Da Pra Assicurazioni si occupa dell'intera procedura: calcoliamo il risparmio, gestiamo i tempi di disdetta e attiviamo la nuova polizza senza interruzioni di copertura.",
  },
  {
    q: "Come funziona la classe di merito?",
    a: "La classe di merito è il sistema bonus-malus che premia gli automobilisti virtuosi. Si parte dalla classe 14 (peggiore) e si scala verso la classe 1 (migliore): ogni anno senza sinistri con colpa si scende di una classe, ogni sinistro fa risalire di due classi. Più bassa è la classe, più bassa è la RCA. La classe di merito è personale ma, grazie alla Legge Bersani, può essere trasferita a un familiare convivente che acquista una nuova auto.",
  },
  {
    q: "Quali documenti servono per il preventivo auto?",
    a: "Per ottenere un preventivo accurato bastano: dati del veicolo (targa o marca/modello/anno), dati anagrafici del proprietario e del conducente principale, attestato di rischio (se hai già una polizza) o classe di merito attuale, eventuale storico sinistri degli ultimi 5 anni. Puoi inviarci tutto direttamente dal form preventivo.",
  },
];

function ReviewCard({ r }) {
  return (
    <div className="bg-white border border-slate-200 p-7 transition-all hover:shadow-xl hover:-translate-y-0.5">
      <div className="flex gap-0.5 text-[#C5A059]">
        {Array.from({ length: r.stars }).map((_, i) => (
          <Star key={i} size={16} fill="#C5A059" strokeWidth={0} />
        ))}
      </div>
      <p className="mt-5 text-slate-700 leading-relaxed">"{r.text}"</p>
      <div className="mt-6 pt-5 border-t border-slate-100">
        <div className="font-medium text-slate-900">{r.name}</div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">{r.role}</div>
      </div>
    </div>
  );
}

export default function AutoMilano() {
  useEffect(() => {
    // SEO meta
    document.title = "Assicurazione Auto Milano — Preventivo RCA | Da Pra Assicurazioni";
    const ensureMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    ensureMeta("description", "Assicurazione auto Milano: preventivo gratuito RCA, kasko, furto e incendio, cristalli. Da Pra Assicurazioni, agenzia assicurativa plurimandataria dal 1958, confronta le migliori compagnie per te.");
    ensureMeta("keywords", "assicurazione auto Milano, preventivo auto Milano, agenzia assicurativa Milano, RCA Milano, polizza auto Milano, assicurazione macchina Milano, plurimandataria Milano");
    ensureMeta("og:title", "Assicurazione Auto Milano — Preventivo RCA | Da Pra Assicurazioni", "property");
    ensureMeta("og:description", "Preventivo gratuito assicurazione auto a Milano. Agenzia plurimandataria dal 1958, confrontiamo le migliori compagnie per te.", "property");
    ensureMeta("og:type", "website", "property");
    ensureMeta("og:url", "https://dapraassicurazioni.it/assicurazione-auto-milano", "property");
    // canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", "https://dapraassicurazioni.it/assicurazione-auto-milano");
    // JSON-LD
    let ld = document.getElementById("ld-auto-milano");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "ld-auto-milano";
      document.head.appendChild(ld);
    }
    ld.text = JSON.stringify(JSON_LD);

    return () => {
      const old = document.getElementById("ld-auto-milano");
      if (old) old.remove();
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Breadcrumb current={{ label: "Assicurazione Auto", href: "/assicurazione-auto-milano" }} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <h1
              data-testid="auto-milano-h1"
              className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-slate-900"
            >
              Assicurazione Auto<br />
              <span className="italic text-slate-800">a Milano.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
              Preventivo gratuito e personalizzato in 24 ore. Confrontiamo per te
              le polizze RCA, kasko, furto e incendio delle migliori compagnie
              assicurative. Tre generazioni di consulenza dedicata in Via Lambro,
              nel cuore di Milano.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/preventivo?type=auto"
                data-testid="auto-milano-cta-preventivo"
                className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white h-12 px-7 text-sm tracking-wide transition-colors"
              >
                Richiedi un Preventivo <ChevronRight size={16} />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-md">
              <div className="border-l border-slate-200 pl-3 sm:pl-4">
                <div className="font-serif text-2xl sm:text-3xl text-slate-900">1958</div>
                <div className="overline mt-1 text-[10px]">Fondazione</div>
              </div>
              <div className="border-l border-slate-200 pl-3 sm:pl-4">
                <div className="font-serif text-2xl sm:text-3xl text-slate-900">3</div>
                <div className="overline mt-1 text-[10px]">Generazioni</div>
              </div>
              <div className="border-l border-slate-200 pl-3 sm:pl-4">
                <div className="font-serif text-2xl sm:text-3xl text-slate-900">24h</div>
                <div className="overline mt-1 text-[10px]">Risposta</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 hidden md:block">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/13861/IMG_3496bfree.jpg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700"
                alt="Assicurazione auto Milano — Da Pra Assicurazioni"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* GARANZIE / BENEFITS */}
      <section className="bg-white border-y border-slate-200 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="overline">Coperture auto</div>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-slate-900">
              Tutte le garanzie per la tua auto, in un'unica polizza su misura.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Componi la copertura ideale partendo dalla RCA obbligatoria, aggiungendo
              le garanzie accessorie che meglio proteggono il tuo veicolo e il tuo budget.
            </p>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  data-testid={`benefit-${b.title.toLowerCase().replace(/\s/g, '-')}`}
                  className="group p-7 bg-[#FAF9F6] border border-slate-200 hover:border-slate-900 transition-all hover:shadow-lg"
                >
                  <span className="w-12 h-12 flex items-center justify-center bg-slate-900 text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-slate-900 transition-colors">
                    <Icon size={20} />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="overline">Perché sceglierci</div>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-slate-900">
              Siamo un'agenzia assicurativa plurimandataria di Milano specializzata in consulenza assicurativa personalizzata.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Collaboriamo con diverse compagnie selezionate per aiutarti a
              trovare soluzioni adatte alle tue esigenze, con un approccio
              orientato a chiarezza, affidabilità e continuità nel tempo.
            </p>
            <Link
              to="/preventivo?type=auto"
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-slate-900 hover:text-[#C5A059] transition-colors"
            >
              Inizia ora il tuo preventivo <ChevronRight size={16} />
            </Link>
          </div>

          <div className="md:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { icon: Sparkles, title: "Confronto multi-compagnia", desc: "Confrontiamo le soluzioni disponibili tra più compagnie in base al profilo del cliente." },
              { icon: Users,    title: "Consulenza dedicata",       desc: "Un referente unico per tutte le tue polizze, presente in agenzia in Via Lambro." },
              { icon: Handshake,title: "Trasparenza totale",         desc: "Nessuna pressione commerciale, nessun costo nascosto. Solo i numeri." },
              { icon: Award,    title: "Tre generazioni a Milano",   desc: "Dal 1958 lavoriamo con famiglie e imprese milanesi. La fiducia si costruisce col tempo." },
            ].map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="border-l-2 border-[#C5A059] pl-5 py-2">
                  <Icon size={18} className="text-[#C5A059]" />
                  <div className="mt-3 font-serif text-lg text-slate-900">{it.title}</div>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">{it.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white border-y border-slate-200 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="overline">Recensioni clienti</div>
              <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-slate-900">
                Cosa dicono di noi.
              </h2>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <div className="flex gap-0.5 text-[#C5A059]">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={18} fill="#C5A059" strokeWidth={0} />
                ))}
              </div>
              <span className="font-medium text-slate-900">5/5</span>
              <span>· Recensioni clienti</span>
            </div>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {REVIEWS.map((r, i) => <ReviewCard key={i} r={r} />)}
          </div>
          <p className="mt-8 text-center text-xs text-slate-500">
            Recensioni reali raccolte dai nostri clienti su Google.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10">
          <div className="text-center">
            <div className="overline">Domande frequenti</div>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-slate-900">
              FAQ Assicurazione Auto Milano.
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left font-serif text-lg md:text-xl text-slate-900 hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm md:text-base leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer />
    </div>
  );
}
